import {
  type JSX,
  createSignal,
  onMount,
  createEffect,
  createResource,
  Show,
  createMemo,
  untrack,
} from 'solid-js';
import isEqual from 'fast-deep-equal';
import { UnreachableCaseError } from 'ts-essentials';
import { QueryClient, QueryClientProvider } from '@tanstack/solid-query';
import { translate } from '@fulcrum/lib';
import { type ITagItem } from '@tcweb/connect-ui';
import {
  TcIssueManagementProps,
  IRightPanelViewMode,
  IUser,
  I3DCallbacks,
  IssueManagementMode,
  IssueLinkWithIssueId,
  PanelDisplayRequest,
  IssueManagementWidgetMode,
  IFeatureConfig,
  IssueReservedViewDataConfig,
  IssueExchangeStatus,
  IssueMarkerInfo,
  ITrackedExportJob,
} from './types/issue.types';
import type { IExportJobDetail } from '@trimble/tc-issue-management-providers';
import { IssueList } from './modules/IssueList/IssueList';
import { IssueRightPanel } from './modules/IssueRightPanel/IssueRightPanel';
import { IssuePanel } from './modules/IssuePanel/IssuePanel';
import { IssueExchangeWidget } from './modules/IssueExchangeWidget';
import {
  ApiServiceProvider,
  ProjectRegionProvider,
  ConfigurationProvider,
  useProjectRegionData,
  useApiServices,
} from '@trimble/tc-issue-management-providers';
import { initWebComponents } from './init-web-components';
import type {
  IssueResponse,
  IconsDetails,
} from '@data-access/connect/tc-issue-management';
import {
  useProjectQueries,
  useQueryStates,
  useTransformedData,
  useContextId,
} from './hooks';
import { LoadingSkeleton } from './components';
import { SnackbarProvider } from './components/ConnectSnackbar';
import { setConfiguration } from '@trimble/tc-issue-management-web3d-utilities';
import { buildIconsList, type IssueIcon } from './utils/issue-icons';
import { toIssueMarkerInfo } from './utils/issue-marker-info';
// Import to get TypeScript declarations - registration handled in init-web-components.ts
import type {} from '@tcweb/connect-ui';

// Local TypeScript declaration for web component usage - registration centralized in init-web-components.ts
declare module 'solid-js' {
  // eslint-disable-next-line @typescript-eslint/no-namespace -- JSX namespace is required for SolidJS intrinsic element declarations
  namespace JSX {
    interface IntrinsicElements {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Web component names use kebab-case
      'tc-restricted-access-splash-screen': {
        title?: string;
        subHeading?: string;
        description?: string;
        buttonLabel?: string;
        buttonType?: 'primary' | 'upgrade';
        showIcon?: boolean;
        showBackgroundImage?: boolean;
        onButtonClick?(): void;
      };
    }
  }
}
import './../styles/topics.css';

// Initialize all required web components early
initWebComponents();

const ISSUES_SNAPSHOT_FIELDS = [
  'marker',
  'priority',
  'status',
  'type',
] as const;

function isIssueLinkWithIssueId(item: unknown): item is IssueLinkWithIssueId {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    typeof (item as { id: unknown }).id === 'string' &&
    'issueId' in item &&
    typeof (item as { issueId: unknown }).issueId === 'string'
  );
}

// Transform API response to UI data format (same as in IssueList)
function transformIssueResponse(apiIssue: IssueResponse): IssueResponse {
  return {
    ...apiIssue,
    assignees: apiIssue.assignees || [],
    dueDate: apiIssue.dueDate || '',
  };
}

// Render error state for region data loading failures
function renderErrorState(
  errorInfo?:
    | {
        title?: string;
        message?: string;
        icon?: string;
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- errorInfo can be various error types from different sources
    | any
): JSX.Element {
  // Handle both structured error info and raw error objects
  const title = errorInfo?.title || 'Region Data Loading Error';
  const message =
    errorInfo?.message || errorInfo || translate('TC.Common.TryAgainLater');
  const icon = errorInfo?.icon || 'icon-font tc-icon-warning';

  return (
    <div class="w-full h-full relative overflow-hidden bg-white flex items-center justify-center">
      <div class="w-full h-full flex items-center justify-center">
        <div class="trimble-tabs-content-pane w-full">
          <div class="flex-1 flex items-center justify-center">
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore - tc-empty is a custom web component not recognized by TypeScript */}
            <tc-empty
              title={title}
              message={message}
              icon={icon}
              class="tc-empty-icon-large"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// This code is only for TypeScript
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- TanStack Query uses this global property name
    __TANSTACK_QUERY_CLIENT__: import('@tanstack/solid-query').QueryClient;
  }
}

// Constants for query configuration
const STALE_TIME_MINUTES = 5;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const STALE_TIME_MS =
  STALE_TIME_MINUTES * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;

// Create QueryClient for API state management
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true, // Allow refetching on mount for fresh data
      refetchOnReconnect: false,
      retry: false,
      staleTime: STALE_TIME_MS, // 5 minutes instead of Infinity to allow data to become stale
    },
  },
});

// This code is for all users
window.__TANSTACK_QUERY_CLIENT__ = queryClient;

// Component that handles region data fetching
function TcIssueManagementWithRegion(
  props: Readonly<
    Pick<
      TcIssueManagementProps,
      | 'projectId'
      | 'projectLocation'
      | 'accessToken'
      | 'threeDCallbacks'
      | 'displayRequest'
      | 'prefetchIssueLinksOnSelect'
      | 'prefetchAllIssues'
      | 'prefetchAllLinks'
      | 'onIssuesSnapshotLoaded'
      | 'onLinksSnapshotLoaded'
      | 'onPanelClose'
      | 'setWidgetMode'
      | 'clearExports'
      | 'addExports'
      | 'featureConfig'
      | 'reservedViewDataConfig'
      | 'refetchSnapshotsOnMutation'
      | 'showAllMarker'
      | 'modelIds'
    >
  >
): JSX.Element {
  const { data: projectRegionData, actions: projectRegionActions } =
    useProjectRegionData();

  // Automatically fetch region data when component mounts
  onMount(async () => {
    if (!projectRegionData().region && !projectRegionData().isLoading) {
      await projectRegionActions.fetchRegionsAndProject(
        props.projectId,
        props.accessToken,
        props.projectLocation
      );
    }
  });

  // Show loading state until region data is loaded
  return (
    <Show
      when={
        !projectRegionData().isLoading &&
        projectRegionData().region &&
        !projectRegionData().error
      }
      fallback={
        projectRegionData().error ? (
          renderErrorState({
            title: 'Region Data Loading Error',
            message:
              typeof projectRegionData().error === 'object' &&
              projectRegionData().error
                ? (projectRegionData().error as any)?.message
                : (projectRegionData().error as string) ||
                  translate('TC.Common.TryAgainLater'),
            icon: 'icon-font tc-icon-warning',
          })
        ) : (
          <LoadingSkeleton />
        )
      }
    >
      <ApiServiceProvider
        accessToken={props.accessToken}
        projectId={props.projectId}
        location={projectRegionData().project?.location}
      >
        <MainWindowContent
          projectId={props.projectId}
          accessToken={props.accessToken}
          region={projectRegionData().region}
          threeDCallbacks={props.threeDCallbacks}
          displayRequest={props.displayRequest}
          prefetchIssueLinksOnSelect={props.prefetchIssueLinksOnSelect}
          prefetchAllIssues={props.prefetchAllIssues}
          prefetchAllLinks={props.prefetchAllLinks}
          onIssuesSnapshotLoaded={props.onIssuesSnapshotLoaded}
          onLinksSnapshotLoaded={props.onLinksSnapshotLoaded}
          onPanelClose={props.onPanelClose}
          setWidgetMode={props.setWidgetMode}
          clearExports={props.clearExports}
          addExports={props.addExports}
          featureConfig={props.featureConfig}
          refetchSnapshotsOnMutation={props.refetchSnapshotsOnMutation}
          reservedViewDataConfig={props.reservedViewDataConfig}
          showAllMarker={props.showAllMarker}
          modelIds={props.modelIds}
        />
      </ApiServiceProvider>
    </Show>
  );
}

function SearchTagEditor(
  props: Readonly<{
    predefinedValues: ITagItem[];
    searchQuery: string;
    setSearchQuery(query: string): void;
    selectedTagList: ITagItem[];
    setSelectedTagList(tags: ITagItem[]): void;
    isSearchMode: boolean;
    setIsSearchMode(isSearchMode: boolean): void;
  }>
): JSX.Element {
  return (
    <div class="search-tag-editor-container relative z-dropdown">
      {/* @ts-expect-error - tc-tag-editor is a custom web component not recognized by TypeScript */}
      <tc-tag-editor
        place-holder={translate('TC.Common.Search')}
        enable-search-icon={true}
        allow-custom-tag={false}
        match-case={false}
        selected-tag-list={props.selectedTagList}
        predefined-tag-list={props.predefinedValues}
        set-focus={props.isSearchMode}
        tag-list-title={'SEARCH BY'}
        not-found-error-msg={translate('NO_RESULTS_FOUND')}
        style={{ ['z-index']: 1000 }}
        on:type-text={(e: CustomEvent): void => {
          props.setSearchQuery(e.detail.query);
        }}
        on:tag-change={(e: CustomEvent): void => {
          props.setSelectedTagList(e.detail.tags);
        }}
        on:search-close={(_e: CustomEvent): void => {
          if (props.selectedTagList.length > 0) {
            props.setSelectedTagList([]);
          } else {
            props.setIsSearchMode(false);
            props.setSelectedTagList([]);
            props.setSearchQuery('');
          }
        }}
      />
    </div>
  );
}

function MainWindowContent(
  props: Readonly<{
    projectId: string;
    accessToken: string;
    region: string;
    threeDCallbacks?: I3DCallbacks;
    displayRequest?: PanelDisplayRequest;
    prefetchIssueLinksOnSelect?: boolean;
    prefetchAllIssues?: boolean;
    prefetchAllLinks?: boolean;
    reservedViewDataConfig?: IssueReservedViewDataConfig;
    onIssuesSnapshotLoaded?(issues: readonly IssueResponse[]): void;
    onLinksSnapshotLoaded?(links: readonly IssueLinkWithIssueId[]): void;
    onPanelClose?(): void;
    setWidgetMode?(mode: IssueManagementWidgetMode): void;
    clearExports?(): void;
    addExports?(jobs: IExportJobDetail[]): void;
    featureConfig?: IFeatureConfig;
    refetchSnapshotsOnMutation?: boolean;
    showAllMarker?: boolean;
    modelIds?: string[];
  }>
): JSX.Element {
  // UI state management - initialize with defaults, then sync via createEffect
  const [isPanelOpen, setIsPanelOpen] = createSignal(false);
  const [selectedIssue, setSelectedIssue] = createSignal<IssueResponse | null>(
    null
  );
  const [viewPort, setViewPort] = createSignal<IRightPanelViewMode>(
    untrack(() => props.displayRequest?.mode) === IssueManagementMode.Create
      ? 'displayCreatePanel'
      : 'displayDetailsPanel'
  );

  // Widget state management
  const [exportJobs, setExportJobs] = createSignal<ITrackedExportJob[]>([]);
  const [widgetMode, setWidgetModeState] =
    createSignal<IssueManagementWidgetMode>(
      IssueManagementWidgetMode.IssueExport
    );
  const [currentExportBatchNumber, setCurrentExportBatchNumber] =
    createSignal(0);

  // Widget callbacks
  const handleSetWidgetMode = (mode: IssueManagementWidgetMode): void => {
    setWidgetModeState(mode);
    props.setWidgetMode?.(mode);
  };

  // Called from IssueExport.onMount when the export dialog opens. Preserves
  // in-progress jobs so their polling and widget state survive the dialog open.
  const handleDialogOpenClear = (): void => {
    setExportJobs((prev) =>
      prev.filter((job) => job.status === IssueExchangeStatus.Processing)
    );
    if (exportJobs().length === 0) {
      setCurrentExportBatchNumber(0);
    }
    props.clearExports?.();
  };

  // Called from the widget's close button. The user explicitly dismissed the
  // widget, so all jobs and batch state are cleared unconditionally.
  const handleClearExports = (): void => {
    setExportJobs([]);
    setCurrentExportBatchNumber(0);
    props.clearExports?.();
  };

  const handleAddExports = (jobs: IExportJobDetail[]): void => {
    const lastIndex = exportJobs().length;
    const batchNumber = currentExportBatchNumber() + 1;
    const newItems: ITrackedExportJob[] = jobs.map((job, index) => ({
      ...job,
      id: `item-${lastIndex + index}`,
      status: job.status || IssueExchangeStatus.Processing,
      batchNumber,
    }));
    setExportJobs((prev) => [...newItems, ...prev]);
    setCurrentExportBatchNumber(batchNumber);
    props.addExports?.(jobs);
  };

  const handleUpdateJob = (job: IExportJobDetail): void => {
    setExportJobs((prev) =>
      prev.map((j) => (j.jobId === job.jobId ? { ...j, ...job } : j))
    );
  };
  const [sortOption, setSortOption] = createSignal<string>(
    translate('TC.Common.CreationDate')
  );
  const [filterOption, setFilterOption] = createSignal<string>(
    translate('TC.Topic.ActiveTopics3D')
  );
  const [isSearchMode, setIsSearchMode] = createSignal<boolean>(false);
  const [searchQuery, setSearchQuery] = createSignal<string>('');
  const [selectedTagList, setSelectedTagList] = createSignal<ITagItem[]>([]);

  // Custom hooks for data management
  // Wrap hook calls in createMemo to track reactive props
  const contextIdMemo = createMemo(() => {
    // Access reactive props within tracked scope
    const projectId = props.projectId;
    const region = props.region;
    const contextIdAccessor = useContextId(projectId, region);
    return contextIdAccessor;
  });
  // Reactive access to props for proper SolidJS reactivity
  const queriesMemo = createMemo(() => {
    // Access reactive props within tracked scope
    const projectId = props.projectId;
    const contextIdValue = contextIdMemo()();
    const queriesResult = useProjectQueries(projectId, contextIdValue);
    return queriesResult;
  });
  // Access queriesMemo in tracked scope for useQueryStates
  const queryStatesMemo = createMemo(() => {
    const queries = queriesMemo();
    return useQueryStates(queries);
  });
  const [transformedData, setTransformedData] = createSignal<ReturnType<
    typeof useTransformedData
  > | null>(null);
  const apiServices = useApiServices();
  const [isSortOrderDesc, setIsSortOrderDesc] = createSignal<boolean>(false);

  // Snapshot data via createResource — automatic disposal of stale fetches
  const [issuesSnapshotData, { refetch: refetchIssuesSnapshot }] =
    createResource<readonly IssueResponse[] | null, string>(
      // eslint-disable-next-line sonarjs/function-return-type -- resource source returns false to skip fetching, per Solid createResource API
      () => {
        const contextId = contextIdMemo()();
        if (
          !(props.prefetchAllIssues || props.refetchSnapshotsOnMutation) ||
          !contextId ||
          !apiServices.issueApi
        )
          return false;
        return contextId;
      },
      async (contextId, { value }) => {
        try {
          const response = await apiServices.issueApi!.fetchIssuesSnapshot(
            contextId,
            { fields: [...ISSUES_SNAPSHOT_FIELDS] }
          );
          return (response.items as IssueResponse[]) ?? value ?? null;
        } catch (error) {
          console.warn(
            '[TcIssueManagement] Failed to fetch issues snapshot:',
            error
          );
          return value ?? null;
        }
      },
      { initialValue: null }
    );

  const [linksSnapshotData, { refetch: refetchLinksSnapshot }] = createResource<
    readonly IssueLinkWithIssueId[] | null,
    string
  >(
    // eslint-disable-next-line sonarjs/function-return-type -- resource source returns false to skip fetching, per Solid createResource API
    () => {
      const contextId = contextIdMemo()();
      if (
        !(props.prefetchAllLinks || props.refetchSnapshotsOnMutation) ||
        !contextId ||
        !apiServices.issueApi
      )
        return false;
      return contextId;
    },
    async (contextId, { value }) => {
      try {
        const response =
          await apiServices.issueApi!.fetchLinksSnapshot(contextId);
        return response.items?.filter(isIssueLinkWithIssueId) ?? value ?? null;
      } catch (error) {
        console.warn(
          '[TcIssueManagement] Failed to fetch links snapshot:',
          error
        );
        return value ?? null;
      }
    },
    { initialValue: null }
  );

  // Call callback when issues snapshot data is ready
  createEffect(() => {
    const data = issuesSnapshotData();
    if (data && props.onIssuesSnapshotLoaded) {
      props.onIssuesSnapshotLoaded(data);
    }
  });

  // Call callback when links snapshot data is ready
  createEffect(() => {
    const data = linksSnapshotData();
    if (data && props.onLinksSnapshotLoaded) {
      props.onLinksSnapshotLoaded(data);
    }
  });

  // Normalized list of default + custom icons for type icon rendering
  // in IssueCard (list) and IssueRightPanel. Uses un-prefixed ids matching
  // legacy tcweb behavior.
  const iconsListMemo = createMemo<IssueIcon[]>(() => {
    const queries = queriesMemo();
    const defaults = queries.getDefaultIconsQuery.data?.items as
      | IconsDetails[]
      | undefined;
    const customs = queries.listIconsQuery()?.data?.items as
      | IconsDetails[]
      | undefined;
    return buildIconsList(defaults, customs);
  });

  // Initialize marker visuals with API configuration and resolved icons.
  // The web3d utilities require a Map keyed by both the bare id and the
  // `<prefix>:<id>` form, so we still build that Map here in addition to the
  // simpler iconsList above.

  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 23 > 15 allowed; refactor later
  createEffect(() => {
    const configData = transformedData()?.configurationData?.();
    if (!configData) return;

    const queries = queriesMemo();

    // Don't initialize until default icons query has settled (success or error)
    if (queries.getDefaultIconsQuery.isPending) return;

    const listIconsResult = queries.listIconsQuery();
    if (listIconsResult !== null && listIconsResult.isPending) return;

    const iconMap = new Map<string, string>();

    const addIconToMap = (
      id: string,
      content: string,
      prefix: string
    ): void => {
      iconMap.set(id, content);
      iconMap.set(`${prefix}:${id}`, content);
    };

    const defaultIconsData = queries.getDefaultIconsQuery.data;
    if (defaultIconsData?.items) {
      for (const icon of defaultIconsData.items) {
        if (icon.id && icon.iconContent) {
          addIconToMap(icon.id, icon.iconContent, 'default');
        }
      }
    }

    if (listIconsResult?.data) {
      const items = listIconsResult.data.items as IconsDetails[] | undefined;
      if (items) {
        for (const icon of items) {
          if (icon.id && icon.iconContent) {
            addIconToMap(icon.id, icon.iconContent, 'custom');
          }
        }
      }
    }

    setConfiguration(configData, iconMap.size > 0 ? iconMap : undefined);
  });

  // Notify host with prepared marker data whenever the visible issue set or
  // toggle state changes. Mirrors TCWEB's renderMarkers(showAllMarker) pattern.
  createEffect(() => {
    const onMarkersChange = props.threeDCallbacks?.onMarkersChange;
    if (!onMarkersChange) return;

    const showAll = props.showAllMarker ?? false;

    if (!showAll) {
      const selected = selectedIssue();
      // Prefer the refreshed issue from the query so the marker reflects
      // edits made after selection; fall back to the selection snapshot.
      const freshSelected = selected
        ? (transformedData()
            ?.issuesData?.()
            ?.items?.find((issue) => issue.id === selected.id) ?? selected)
        : null;
      const selectedMarker = toIssueMarkerInfo(freshSelected);
      onMarkersChange(selectedMarker ? [selectedMarker] : [], showAll);
      return;
    }

    const issuesResult = transformedData()?.issuesData?.();
    if (!issuesResult?.items) {
      onMarkersChange([], showAll);
      return;
    }

    const markers = issuesResult.items
      .map(toIssueMarkerInfo)
      .filter((marker): marker is IssueMarkerInfo => marker !== null);

    onMarkersChange(markers, showAll);
  });

  const refetchSnapshots = (): void => {
    void refetchIssuesSnapshot();
    void refetchLinksSnapshot();
  };

  // React to displayRequest changes for panel navigation
  const [lastProcessedRequest, setLastProcessedRequest] =
    createSignal<PanelDisplayRequest | null>(null);

  createEffect(() => {
    const request = props.displayRequest;
    if (!request) {
      setLastProcessedRequest(null);
      return;
    }

    // Helper to compare markerData for Create mode requests
    const markerDataChanged = (
      last: PanelDisplayRequest | null,
      current: PanelDisplayRequest
    ): boolean => {
      if (current.mode !== IssueManagementMode.Create) return false;
      if (!last || last.mode !== IssueManagementMode.Create) return true;

      const lastMarker = last.markerData;
      const currentMarker = current.markerData;

      // Both undefined - same
      if (!lastMarker && !currentMarker) return false;
      // One undefined, one not - different
      if (!lastMarker || !currentMarker) return true;

      // Compare point coordinates
      if (
        lastMarker.point.x !== currentMarker.point.x ||
        lastMarker.point.y !== currentMarker.point.y ||
        lastMarker.point.z !== currentMarker.point.z
      ) {
        return true;
      }

      // Compare locationContext (if present)
      const lastContext = lastMarker.locationContext;
      const currentContext = currentMarker.locationContext;
      if (!lastContext && !currentContext) return false;
      if (!lastContext || !currentContext) return true;

      // Deep comparison using fast-deep-equal (handles property order differences)
      return !isEqual(lastContext, currentContext);
    };

    // A ViewIssue request may intentionally repeat for the same issue to reopen its details.
    // Create requests are deduplicated by value to avoid reopening from unrelated renders.
    const lastRequest = lastProcessedRequest();
    const isNewRequest =
      !lastRequest ||
      (request.mode === IssueManagementMode.ViewIssue &&
        request !== lastRequest) ||
      lastRequest.mode !== request.mode ||
      (request.mode === IssueManagementMode.ViewIssue &&
        lastRequest.mode === IssueManagementMode.ViewIssue &&
        lastRequest.issueId !== request.issueId) ||
      markerDataChanged(lastRequest, request);

    if (!isNewRequest) return;

    switch (request.mode) {
      case IssueManagementMode.Create:
        setIsPanelOpen(true);
        setViewPort('displayCreatePanel');
        setLastProcessedRequest(request);
        break;

      case IssueManagementMode.ViewIssue: {
        // Find the issue in the issues data
        const rawIssues = queriesMemo().issuesQuery()?.data?.items;
        const issue = rawIssues?.find(
          (i: IssueResponse) => i.id === request.issueId
        );
        if (issue) {
          setSelectedIssue(issue);
          setIsPanelOpen(true);
          setViewPort('displayDetailsPanel');
          // Only mark as processed after successfully finding and displaying the issue
          setLastProcessedRequest(request);
        }
        // If issue not found, don't set lastProcessedRequest - allows retry when data loads
        break;
      }

      case IssueManagementMode.List:
        // Do nothing - stay on list view
        setLastProcessedRequest(request);
        break;

      default:
        throw new UnreachableCaseError(request);
    }
  });

  createEffect(() => {
    const currentSearchMode = isSearchMode();
    const currentSelectedTags = selectedTagList();
    const searchTags =
      currentSearchMode && currentSelectedTags.length > 0
        ? currentSelectedTags
        : undefined;

    try {
      const newData = useTransformedData(
        queriesMemo(),
        sortOption(),
        filterOption(),
        isSortOrderDesc() ? 'DESC' : 'ASC',
        searchTags
      );
      setTransformedData(newData);
    } catch {
      setTransformedData(null);
    }
  });

  // Access policy checking logic
  // Note: accessRestriction is computed but not currently used in the UI
  // Keeping the logic for future use when restricted access UI is implemented
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS6133: Reserved for future use
  // eslint-disable-next-line sonarjs/no-unused-vars -- reserved for future use (see @ts-ignore above)
  const _accessRestriction = createMemo(() => {
    const accessPolicy = queriesMemo().accessPolicyQuery.data;

    if (!accessPolicy) {
      return null; // Still loading or no policy data
    }

    // TODO: Check for restricted access based on policy type
    const isUnlicensed = accessPolicy.policyType?.includes('UNLICENSED');
    const hasAppAccess = accessPolicy.appAccess !== false;

    if (isUnlicensed || !hasAppAccess) {
      return {
        title: 'Access Restricted',
        subHeading: 'License Required',
        description:
          'Issue Management requires a valid license to access this feature.',
        buttonLabel: 'Contact Administrator',
        buttonType: 'primary' as const,
        showIcon: true,
        showBackgroundImage: false,
      };
    }

    return null; // Access is allowed
  });

  const handleAddIssue = (): void => {
    setViewPort('displayCreatePanel');
  };

  const handleRowClick = async (issueData: IssueResponse): Promise<void> => {
    setSelectedIssue(issueData);
    setIsPanelOpen(true);
    setViewPort('displayDetailsPanel');

    // Call 3D callback for issue item selection
    if (!props.threeDCallbacks?.onIssueItemSelect) return;

    // Capture issue ID to check for race conditions
    const clickedIssueId = issueData.id;
    const currentContextId = contextIdMemo()();

    // Fetch IssueLinks if enabled - silently resolve to undefined on failure
    const issueLinks =
      props.prefetchIssueLinksOnSelect && currentContextId
        ? await apiServices.issueReferenceApi
            .fetchIssueReferences(currentContextId, clickedIssueId)
            .catch(() => undefined)
        : undefined;

    // Check if this issue is still selected (prevent race condition from rapid clicks)
    const currentlySelectedIssue = selectedIssue();
    if (
      !currentlySelectedIssue ||
      currentlySelectedIssue.id !== clickedIssueId
    ) {
      // Issue has changed, ignore this callback
      return;
    }

    const request = {
      type: 'issue-item-select' as const,
      issueId: clickedIssueId,
      contextId: currentContextId,
      payload: {
        issue: issueData,
        ...(issueLinks && { issueLinks }),
      },
    };

    await props.threeDCallbacks.onIssueItemSelect(request).catch((error) => {
      console.warn(
        '[TcIssueManagement] onIssueItemSelect callback failed:',
        error
      );
    });
  };

  const handleSelectIssue = (issueData: IssueResponse): void => {
    setSelectedIssue(issueData);
    // Keep panel open and in the same view mode when navigating
  };

  const handleClosePanel = (): void => {
    setIsPanelOpen(false);
    setSelectedIssue(null);
    setViewPort('displayDetailsPanel');

    // Notify consumer to clear their display request state
    props.onPanelClose?.();
  };

  const handleViewPortChange = (newViewPort: IRightPanelViewMode): void => {
    setViewPort(newViewPort);
  };

  const handleSort = (option: string): void => {
    const currentSort = sortOption();
    if (currentSort === option) {
      setIsSortOrderDesc(!isSortOrderDesc());
    } else {
      setSortOption(option);
      setIsSortOrderDesc(false);
    }
  };

  const handleFilter = (option: string): void => {
    setFilterOption(option);
  };

  const handleSearch = (): void => {
    const newSearchMode = !isSearchMode();
    setIsSearchMode(newSearchMode);
    setSelectedTagList([]);
  };

  const getPredefinedValues = (): ITagItem[] => {
    return [
      {
        isValid: true,
        label: `${translate('TCW_ALL')}: ${searchQuery()}`,
        value: `All:${searchQuery()}`,
      },
      {
        isValid: true,
        label: `${translate('assignee')}: ${searchQuery()}`,
        value: `assignees:${searchQuery()}`,
      },
      {
        isValid: true,
        label: `${translate('TC.Topic.Creator')}: ${searchQuery()}`,
        value: `creation_author_uuid:${searchQuery()}`,
      },
      {
        isValid: true,
        label: `${translate('TC.Common.Description')}: ${searchQuery()}`,
        value: `description:${searchQuery()}`,
      },
      {
        isValid: true,
        label: `${translate('TC.Common.Priority')}: ${searchQuery()}`,
        value: `priority:${searchQuery()}`,
      },
      {
        isValid: true,
        label: `${translate('TC.Common.Status')}: ${searchQuery()}`,
        value: `status:${searchQuery()}`,
      },
      {
        isValid: true,
        label: `${translate('TC.Common.Title')}: ${searchQuery()}`,
        value: `title:${searchQuery()}`,
      },

      {
        isValid: true,
        label: `${translate('TC.Common.Type')}: ${searchQuery()}`,
        value: `type:${searchQuery()}`,
      },

      {
        isValid: true,
        label: `${translate('TCW_Tags')}: ${searchQuery()}`,
        value: `tags:${searchQuery()}`,
      },
    ];
  };

  const handleRefresh = (): void => {
    // Invalidate and refetch all issue-related queries using the global queryClient
    queryClient.invalidateQueries({
      queryKey: ['issues'],
      exact: false, // This will invalidate all queries that start with 'issues'
    });
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS6133: Reserved for future use
  // eslint-disable-next-line sonarjs/no-unused-vars -- reserved for future use (see @ts-ignore above)
  const _handleRestrictedAccessClick = (): void => {
    // Handle contact administrator or upgrade action
    // You can add specific logic here like opening a modal, sending email, etc.
  };

  return (
    <div class="tc-issue-management w-full h-full relative overflow-hidden bg-white">
      <IssuePanel
        title={translate('TC.Common.Topics')}
        titleSectionRight={
          <SearchTagEditor
            predefinedValues={getPredefinedValues()}
            searchQuery={searchQuery()}
            setSearchQuery={setSearchQuery}
            selectedTagList={selectedTagList()}
            setSelectedTagList={setSelectedTagList}
            isSearchMode={isSearchMode()}
            setIsSearchMode={setIsSearchMode}
          />
        }
        isSearchMode={isSearchMode()}
        onAddIssue={handleAddIssue}
        onSort={handleSort}
        isSortOrderDesc={isSortOrderDesc()}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        configurationData={
          transformedData()?.configurationData?.() || undefined
        }
        showAllMarker={
          props.threeDCallbacks ? (props.showAllMarker ?? false) : undefined
        }
        onChangeMarker={props.threeDCallbacks?.onShowAllMarkerChange}
        panelContent={
          <div class="h-full min-h-0">
            <div
              class={`relative h-full min-h-0 ${isPanelOpen() ? 'hidden' : ''}`}
            >
              <IssueList
                projectId={props.projectId}
                contextId={contextIdMemo()() ?? ''}
                accessToken={props.accessToken}
                onRowClick={handleRowClick}
                isLoading={queryStatesMemo().isLoading()}
                hasError={queryStatesMemo().hasError()}
                errorInfo={queryStatesMemo().errorInfo()}
                issuesData={transformedData()?.issuesData?.() || null}
                projectUsersData={transformedData()?.projectUsersData?.() || []}
                followStatusData={
                  transformedData()?.followStatusData?.() || null
                }
                configurationData={
                  transformedData()?.configurationData?.() || undefined
                }
                iconsList={iconsListMemo()}
              />
            </div>
            {/* Right Panel */}
            <IssueRightPanel
              isOpen={isPanelOpen()}
              selectedIssue={selectedIssue()}
              onClose={handleClosePanel}
              viewPort={viewPort()}
              onViewPortChange={handleViewPortChange}
              issuesList={
                (
                  transformedData()?.issuesData?.()?.items as IssueResponse[]
                )?.map(transformIssueResponse) || []
              }
              onSelectIssue={handleSelectIssue}
              projectId={props.projectId}
              currentUser={queriesMemo().userQuery.data as IUser}
              allAssignees={transformedData()?.allAssigneesData?.() || []}
              pendingMarkerData={
                props.displayRequest?.mode === IssueManagementMode.Create
                  ? props.displayRequest.markerData
                  : undefined
              }
              projectTags={transformedData()?.projectTagsData?.() || []}
              configurationData={
                transformedData()?.configurationData?.() || null
              }
              contextId={contextIdMemo()()}
              accessToken={props.accessToken}
              threeDCallbacks={props.threeDCallbacks}
              projectUsersData={transformedData()?.projectUsersData?.() || []}
              projectGroupsData={transformedData()?.projectGroupsData?.() || []}
              followStatusData={transformedData()?.followStatusData?.() || null}
              setWidgetMode={handleSetWidgetMode}
              clearExports={handleDialogOpenClear}
              addExports={handleAddExports}
              featureConfig={props.featureConfig}
              reservedViewDataConfig={props.reservedViewDataConfig}
              onIssueMutated={() => {
                if (props.refetchSnapshotsOnMutation) refetchSnapshots();
              }}
              onIssueDeleted={(_issueId) => {
                if (props.refetchSnapshotsOnMutation) refetchSnapshots();
              }}
              iconsList={iconsListMemo()}
              modelIds={props.modelIds ?? []}
            />
          </div>
        }
      />
      {/* Issue Exchange Widget */}
      <IssueExchangeWidget
        exportJobs={exportJobs()}
        mode={widgetMode()}
        projectId={props.projectId}
        isEmbedded={false}
        currentExportBatchNumber={currentExportBatchNumber()}
        onUpdateJob={handleUpdateJob}
        onClearExports={handleClearExports}
      />
    </div>
  );
}

export function TcIssueManagement(
  props: Readonly<TcIssueManagementProps>
): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigurationProvider environment={props.environment ?? 'stage'}>
        <ProjectRegionProvider>
          <SnackbarProvider>
            <TcIssueManagementWithRegion
              projectId={props.projectId}
              projectLocation={props.projectLocation}
              accessToken={props.accessToken}
              threeDCallbacks={props.threeDCallbacks}
              displayRequest={props.displayRequest}
              prefetchIssueLinksOnSelect={props.prefetchIssueLinksOnSelect}
              prefetchAllIssues={props.prefetchAllIssues}
              prefetchAllLinks={props.prefetchAllLinks}
              onIssuesSnapshotLoaded={props.onIssuesSnapshotLoaded}
              onLinksSnapshotLoaded={props.onLinksSnapshotLoaded}
              onPanelClose={props.onPanelClose}
              setWidgetMode={props.setWidgetMode}
              clearExports={props.clearExports}
              addExports={props.addExports}
              featureConfig={props.featureConfig}
              reservedViewDataConfig={props.reservedViewDataConfig}
              refetchSnapshotsOnMutation={props.refetchSnapshotsOnMutation}
              showAllMarker={props.showAllMarker}
              modelIds={props.modelIds ?? []}
            />
            {props.children}
          </SnackbarProvider>
        </ProjectRegionProvider>
      </ConfigurationProvider>
    </QueryClientProvider>
  );
}
